// The full submission of an intake request, grouped as the submitter
// answered it, for the Drawer's "Use case" section.
//
// Wizard and portal items (request_type 'ai_use_case') carry every answer in
// use_case_payload; the groups follow the wizard's steps and use its English
// question labels. Chat items carry the intake record the intake assistant
// structured from the conversation. Payload keys this file does not know are
// shown under "Other answers", so a new portal question is never hidden.
//
// groupUseCase(item) returns [{ id, title, rows: [{ label, value, kind }] }].
// kind is 'text' (default), 'list' (array of strings) or 'links' (array of
// { name, url }). A row with value null renders as "Not answered".

const FREQUENCY = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
const KPI = {
  time_saved: 'Time saved',
  error_reduction: 'Error reduction',
  cost_reduction: 'Cost reduction',
  user_satisfaction: 'User satisfaction',
  throughput: 'Throughput',
};
const DATA_TYPE = {
  personal_data: 'Personal data',
  patient_data: 'Patient data',
  financial: 'Financial data',
  financial_data: 'Financial data',
  internal_docs: 'Internal documents',
  public_data: 'Public or open data',
};
export const PRIORITY_TIER = {
  fast_track: 'Fast track',
  standard: 'Standard',
  needs_refinement: 'Needs refinement',
};
// Flags the wizard's AI data step can raise (routes/ai_intake.py, step 5).
// An unknown flag falls back to its humanised key.
const GDPR_FLAG = {
  gdpr_article_9: 'Special category personal data (GDPR article 9)',
};
const AI_ACT_FLAG = {
  ai_act_high_risk: 'Possible high risk use under the EU AI Act',
};
const LANGUAGE = { sv: 'Swedish', en: 'English' };

// Keys rendered by a named group, or metadata that is not an answer.
const KNOWN_KEYS = new Set([
  'request_type', 'lang', 'timestamp', 'submitter', 'session_id',
  'problem_statement', 'problem_refined', 'users', 'current_process',
  'success_metrics', 'data', 'alternatives_considered', 'ai_suggested_alternatives',
  'ai_summary', 'impact_score', 'complexity_score', 'priority_tier',
  'attachments', 'links',
]);

const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const positive = (v) => (typeof v === 'number' && v > 0 ? v : null);
const listOf = (v, labels) => {
  const items = (Array.isArray(v) ? v : [])
    .map((x) => (labels ? labels[x] || humanize(String(x)) : String(x).replace(/^[-•]\s*/, '').trim()))
    .filter(Boolean);
  return items.length ? items : null;
};

export function humanize(key) {
  const s = String(key).replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

function safeUrl(url) {
  try {
    const u = new URL(String(url));
    return ['http:', 'https:'].includes(u.protocol) ? u.href : null;
  } catch {
    return null;
  }
}

// Attachments and links as { name, url }; anything without an http(s) URL
// is dropped rather than rendered as a link.
export function linksOf(value) {
  const items = (Array.isArray(value) ? value : [])
    .map((x) => {
      const url = safeUrl(typeof x === 'string' ? x : x?.url || x?.href);
      if (!url) return null;
      const name = (typeof x === 'object' && (x?.name || x?.title || x?.filename)) || url;
      return { name: String(name), url };
    })
    .filter(Boolean);
  return items.length ? items : null;
}

function otherValue(v) {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('\n') : null;
  if (typeof v === 'object') {
    const lines = Object.entries(v)
      .map(([k, x]) => [humanize(k), otherValue(x)])
      .filter(([, x]) => x !== null)
      .map(([k, x]) => `${k}: ${x}`);
    return lines.length ? lines.join('\n') : null;
  }
  return String(v);
}

const nonEmpty = (groups) => groups.filter((g) => g.rows.length > 0);

function wizardGroups(item) {
  const p = obj(item.use_case_payload);
  const users = obj(p.users);
  const proc = obj(p.current_process);
  const metrics = obj(p.success_metrics);
  const data = obj(p.data);
  const submitter = obj(p.submitter);
  const refined = text(p.problem_refined) || text(item.problem);
  const original = text(p.problem_statement);

  const submittedBy = [
    { label: 'Name', value: text(submitter.name) },
    { label: 'Email', value: text(submitter.email) },
    { label: 'Team', value: text(submitter.team) },
  ];
  // The form language alone says nothing about who submitted, so the group
  // shows only when there is a name, email or team.
  if (submittedBy.some((r) => r.value !== null)) {
    submittedBy.push({ label: 'Form language', value: LANGUAGE[p.lang] || text(p.lang) });
  }

  const groups = [
    {
      id: 'assessment',
      title: 'AI assessment at submission',
      rows: [
        { label: 'AI summary', value: text(p.ai_summary) || text(item.ai_summary) },
        { label: 'Priority', value: PRIORITY_TIER[p.priority_tier || item.priority_tier] || text(p.priority_tier) },
        { label: 'Impact (1 to 5)', value: positive(p.impact_score) },
        { label: 'Complexity (1 to 5)', value: positive(p.complexity_score) },
      ],
    },
    {
      id: 'problem',
      title: 'Problem',
      rows: [
        { label: 'What problem are you trying to solve?', value: refined, always: true },
        ...(original && original !== refined
          ? [{ label: "In the submitter's own words, before AI refinement", value: original }]
          : []),
      ],
    },
    {
      id: 'users',
      title: 'Users',
      rows: [
        { label: 'Who is affected?', value: text(users.description), always: true },
        { label: 'Number of people', value: positive(users.count) },
        { label: 'How often?', value: FREQUENCY[users.frequency] || text(users.frequency) },
        { label: 'Estimated hours affected per week', value: positive(users.hours_impacted_weekly) },
      ],
    },
    {
      id: 'process',
      title: 'Current process',
      rows: [
        { label: 'How is this done today?', value: text(proc.description), always: true },
        { label: 'Minutes per task', value: positive(proc.minutes_per_task) },
        { label: 'Volume per week', value: positive(proc.volume_per_week) },
        { label: 'Estimated cost per year', value: positive(proc.hours_per_year) && `${proc.hours_per_year} hours` },
      ],
    },
    {
      id: 'success',
      title: 'Success',
      rows: [
        { label: 'What does success look like in 6 months?', value: text(metrics.description) || text(item.success_criteria), always: true },
        { label: 'KPIs', value: listOf(metrics.kpis, KPI), kind: 'list' },
      ],
    },
    {
      id: 'data',
      title: 'Data and privacy',
      rows: [
        { label: 'Data types', value: listOf(data.types, DATA_TYPE), kind: 'list', always: true },
        { label: 'Data involved', value: text(data.description) },
        { label: 'AI assessed sensitivity tier', value: text(data.sensitivity_tier) },
        { label: 'GDPR flags', value: listOf(data.gdpr_flags, GDPR_FLAG), kind: 'list' },
        { label: 'AI Act flags', value: listOf(data.ai_act_flags, AI_ACT_FLAG), kind: 'list' },
      ],
    },
    {
      id: 'alternatives',
      title: 'Alternatives',
      rows: [
        { label: 'Have you considered existing tools or approaches?', value: text(p.alternatives_considered), always: true },
        { label: 'AI suggested alternatives', value: listOf(p.ai_suggested_alternatives), kind: 'list' },
      ],
    },
    {
      id: 'attachments',
      title: 'Attachments and links',
      rows: [
        { label: 'Attachments', value: linksOf(p.attachments), kind: 'links' },
        { label: 'Links', value: linksOf(p.links), kind: 'links' },
      ],
    },
    {
      id: 'submitter',
      title: 'Submitted by',
      rows: submittedBy,
    },
  ];

  const other = Object.entries(p)
    .filter(([k]) => !KNOWN_KEYS.has(k))
    .map(([k, v]) => ({ label: humanize(k), value: otherValue(v) }))
    .filter((r) => r.value !== null);
  if (other.length) groups.push({ id: 'other', title: 'Other answers', rows: other });

  return finish(groups);
}

function recordGroups(item) {
  const fromChat = item.source === 'chat';
  return finish([
    {
      id: 'record',
      title: fromChat ? 'Intake record from the intake assistant' : 'Request',
      rows: [
        { label: 'Problem', value: text(item.problem), always: true },
        { label: 'Users affected', value: text(item.users_affected), always: true },
        { label: 'Success criteria', value: text(item.success_criteria), always: true },
        { label: 'Business value', value: text(item.business_value) },
        { label: 'Product', value: text(item.product) },
        { label: 'Tags', value: listOf(item.tags), kind: 'list' },
      ],
    },
  ]);
}

// Rows that are empty and not a main question are dropped; a main question
// left empty stays, shown as "Not answered". Groups left with no rows are
// dropped.
function finish(groups) {
  return nonEmpty(groups.map((g) => ({
    ...g,
    rows: g.rows
      .filter((r) => r.always || (r.value !== null && r.value !== undefined && r.value !== false))
      .map(({ always, ...r }) => ({ kind: 'text', ...r, value: r.value === undefined || r.value === false ? null : r.value })),
  })));
}

export function isWizardSubmission(item) {
  return item?.request_type === 'ai_use_case' && Object.keys(obj(item?.use_case_payload)).length > 0;
}

export function groupUseCase(item) {
  if (!item) return [];
  return isWizardSubmission(item) ? wizardGroups(item) : recordGroups(item);
}
